import { useEffect, useState } from 'react';
import { getRouteMeta } from '../../routeMetadata';
import { shelfForLink } from '../Product/productData';

const BAR_HEIGHT_PX = 56;
// Below this size a heading is a label inside a full-screen tool, not a large title.
const LARGE_TITLE_MIN_FONT_PX = 20;

export function pageTitle(pathname: string, search = ''): string {
  const path = pathname.replace(/\/+$/, '');
  if (!path) {
    return shelfForLink(new URLSearchParams(search))?.name ?? 'Home';
  }
  return getRouteMeta(path)?.title ?? '';
}

function largeTitleInView(): boolean {
  const heading = document.querySelector('main h1');
  if (!heading) return false;
  const box = heading.getBoundingClientRect();
  if (box.width <= 1 || box.height <= 1) return false;
  if (parseFloat(getComputedStyle(heading).fontSize) < LARGE_TITLE_MIN_FONT_PX) return false;
  return box.bottom > BAR_HEIGHT_PX;
}

/**
 * The bar shows the title once the large title scrolls under it, and always on a
 * page that has no large title. Pages load lazily and swap their heading, so the
 * check also runs when the content changes.
 */
export function useShowsBarTitle(location: string): boolean {
  const [shows, setShows] = useState(false);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        // A lazy route has no heading yet. Wait, so the title does not flash in and out.
        if (document.querySelector('main [data-route-loading]')) return;
        setShows(!largeTitleInView());
      });
    };
    setShows(false);
    update();

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const main = document.querySelector('main');
    const observer = new MutationObserver(update);
    if (main) observer.observe(main, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, [location]);

  return shows;
}
